import { useState, type FormEvent } from 'react';
import type {
  Loan,
  LoanEvent,
  LoanEventType,
  DisbursementEvent,
  RateChangeEvent,
  PrepaymentEvent,
  BalanceCorrectionEvent,
  ChargeEvent,
  EmiDateChangeEvent,
  RepaymentConsequence,
} from '@/types';
import { validateEvent } from '@/lib/calc/validation';
import { addEvent, editEvent } from '@/lib/db';
import { todayIsoDate } from '@/lib/format';
import { CurrencyInput } from '@/components/CurrencyInput';
import { RepaymentConsequenceFields } from './RepaymentConsequenceFields';
import { PrepaymentConsequenceFields } from './PrepaymentConsequenceFields';

interface EventFormProps {
  loan: Loan;
  /** Every currently-active event for the loan — needed by the repayment-consequence basis calculation. */
  events: LoanEvent[];
  eventType: LoanEventType;
  /** When editing an existing event, pass it here; omit for creating a new one. */
  existingEvent?: LoanEvent;
  onSaved: () => void;
  onCancel: () => void;
}

/**
 * A single form component that renders the correct fields for whichever
 * event type is being created/edited. This is deliberately one component
 * with a switch inside, rather than 7 near-identical files, since the
 * shared scaffolding (effective date, note, submit/cancel, validation
 * wiring) is large relative to the type-specific fields.
 *
 * PHASE 2: rate_change, prepayment (when principal_prepayment), and
 * balance_correction all REQUIRE a repayment consequence — there is no
 * checkbox to opt out. The consequence sub-forms
 * (RepaymentConsequenceFields / PrepaymentConsequenceFields) calculate a
 * suggested EMI/tenure via the real calculation engine's basis
 * calculation (src/lib/calc/repaymentBasis.ts) rather than asking the
 * user to enter blank free-text values.
 */
export function EventForm({ loan, events, eventType, existingEvent, onSaved, onCancel }: EventFormProps) {
  const isEditing = !!existingEvent;

  const [effectiveDate, setEffectiveDate] = useState(existingEvent?.effectiveDate ?? todayIsoDate());
  const [note, setNote] = useState(existingEvent?.note ?? '');

  const [disbursementAmount, setDisbursementAmount] = useState<number | undefined>(
    existingEvent && existingEvent.type === 'disbursement' ? existingEvent.amount : undefined
  );
  const [trancheLabel, setTrancheLabel] = useState(
    existingEvent && existingEvent.type === 'disbursement' ? (existingEvent.trancheLabel ?? '') : ''
  );

  // Rate change
  const [annualRatePercent, setAnnualRatePercent] = useState(
    existingEvent && existingEvent.type === 'rate_change' ? String(existingEvent.annualRatePercent) : ''
  );
  const [rateChangeConsequence, setRateChangeConsequence] = useState<RepaymentConsequence | undefined>(
    existingEvent && existingEvent.type === 'rate_change' ? existingEvent.repaymentConsequence : undefined
  );

  // Prepayment
  const [prepaymentAmount, setPrepaymentAmount] = useState<number | undefined>(
    existingEvent && existingEvent.type === 'prepayment' ? existingEvent.amount : undefined
  );
  const [paymentType, setPaymentType] = useState<PrepaymentEvent['paymentType']>(
    existingEvent && existingEvent.type === 'prepayment' ? existingEvent.paymentType : 'principal_prepayment'
  );
  const [prepaymentConsequence, setPrepaymentConsequence] = useState<PrepaymentEvent['repaymentConsequence']>(
    existingEvent && existingEvent.type === 'prepayment' ? existingEvent.repaymentConsequence : undefined
  );

  // Balance correction
  const [licConfirmedBalance, setLicConfirmedBalance] = useState<number | undefined>(
    existingEvent && existingEvent.type === 'balance_correction' ? existingEvent.licConfirmedBalance : undefined
  );
  const [balanceCorrectionConsequence, setBalanceCorrectionConsequence] = useState<RepaymentConsequence | undefined>(
    existingEvent && existingEvent.type === 'balance_correction' ? existingEvent.repaymentConsequence : undefined
  );

  // Charge
  const [chargeAmount, setChargeAmount] = useState<number | undefined>(
    existingEvent && existingEvent.type === 'charge' ? existingEvent.amount : undefined
  );
  const [category, setCategory] = useState<ChargeEvent['category']>(
    existingEvent && existingEvent.type === 'charge' ? existingEvent.category : 'insurance'
  );
  const [financedIntoPrincipal, setFinancedIntoPrincipal] = useState(
    existingEvent && existingEvent.type === 'charge' ? existingEvent.financedIntoPrincipal : false
  );

  // EMI date change
  const [newEmiDayOfMonth, setNewEmiDayOfMonth] = useState(
    existingEvent && existingEvent.type === 'emi_date_change' ? String(existingEvent.newEmiDayOfMonth) : '7'
  );

  const [errors, setErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  function buildEventData(): Partial<LoanEvent> {
    const base = { loanId: loan.id, effectiveDate, note: note.trim() || undefined };
    switch (eventType) {
      case 'disbursement':
        return {
          ...base,
          type: 'disbursement',
          amount: disbursementAmount,
          trancheLabel: trancheLabel.trim() || undefined,
        } as Partial<DisbursementEvent>;
      case 'rate_change':
        return {
          ...base,
          type: 'rate_change',
          annualRatePercent: Number(annualRatePercent),
          repaymentConsequence: rateChangeConsequence,
        } as Partial<RateChangeEvent>;
      case 'prepayment':
        return {
          ...base,
          type: 'prepayment',
          amount: prepaymentAmount,
          paymentType,
          repaymentConsequence: paymentType === 'principal_prepayment' ? prepaymentConsequence : undefined,
        } as Partial<PrepaymentEvent>;
      case 'balance_correction':
        return {
          ...base,
          type: 'balance_correction',
          licConfirmedBalance,
          repaymentConsequence: balanceCorrectionConsequence,
        } as Partial<BalanceCorrectionEvent>;
      case 'charge':
        return {
          ...base,
          type: 'charge',
          amount: chargeAmount,
          category,
          financedIntoPrincipal,
        } as Partial<ChargeEvent>;
      case 'emi_date_change':
        return {
          ...base,
          type: 'emi_date_change',
          newEmiDayOfMonth: Number(newEmiDayOfMonth),
        } as Partial<EmiDateChangeEvent>;
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const data = buildEventData();
    const validationErrors = validateEvent(eventType, data);
    setErrors(validationErrors);
    if (validationErrors.length > 0) return;

    setIsSaving(true);
    try {
      if (isEditing && existingEvent) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await editEvent(existingEvent as any, data as any);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await addEvent(data as any);
      }
      onSaved();
    } catch (err) {
      setErrors([err instanceof Error ? err.message : 'Something went wrong saving this event.']);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form className="stack" onSubmit={handleSubmit}>
      {errors.length > 0 && (
        <div className="card" style={{ borderColor: 'var(--color-danger)' }}>
          {errors.map((err) => (
            <p key={err} className="field-error" style={{ margin: 0 }}>
              {err}
            </p>
          ))}
        </div>
      )}

      <div className="field-group">
        <label htmlFor="effectiveDate">Effective Date</label>
        <input
          id="effectiveDate"
          type="date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
        />
        <span className="field-hint">
          Use the lender's stated effective date (e.g. from LIC's letter), not necessarily today's date.
        </span>
      </div>

      {eventType === 'disbursement' && (
        <>
          <div className="field-group">
            <label htmlFor="disbursementAmount">Disbursement Amount</label>
            <CurrencyInput id="disbursementAmount" value={disbursementAmount} onChange={setDisbursementAmount} />
          </div>
          <div className="field-group">
            <label htmlFor="trancheLabel">Tranche Label (optional)</label>
            <input
              id="trancheLabel"
              value={trancheLabel}
              onChange={(e) => setTrancheLabel(e.target.value)}
              placeholder="e.g. Tranche 3 of 5 — plinth completion"
            />
          </div>
        </>
      )}

      {eventType === 'rate_change' && (
        <>
          <div className="field-group">
            <label htmlFor="annualRatePercent">New Interest Rate (% p.a.)</label>
            <input
              id="annualRatePercent"
              type="number"
              step="0.01"
              inputMode="decimal"
              value={annualRatePercent}
              onChange={(e) => setAnnualRatePercent(e.target.value)}
            />
          </div>
          <p className="field-hint">
            A rate change affects your repayment terms — the calculated effect on EMI/tenure is shown below.
          </p>
          <RepaymentConsequenceFields
            loan={loan}
            events={events}
            eventEffectiveDate={effectiveDate}
            liveRateOverride={annualRatePercent !== '' && Number.isFinite(Number(annualRatePercent)) ? Number(annualRatePercent) : undefined}
            value={rateChangeConsequence}
            onChange={setRateChangeConsequence}
          />
        </>
      )}

      {eventType === 'prepayment' && (
        <>
          <div className="field-group">
            <label htmlFor="prepaymentAmount">Amount</label>
            <CurrencyInput id="prepaymentAmount" value={prepaymentAmount} onChange={setPrepaymentAmount} />
          </div>
          <div className="field-group">
            <label htmlFor="paymentType">Payment Type</label>
            <select
              id="paymentType"
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value as PrepaymentEvent['paymentType'])}
            >
              <option value="principal_prepayment">Genuine Principal Prepayment</option>
              <option value="advance_emi">Advance EMI Payment</option>
            </select>
            <span className="field-hint">
              A principal prepayment reduces your outstanding balance immediately. An advance EMI just pays a future
              scheduled EMI early — it does not reduce principal ahead of schedule.
            </span>
          </div>
          {paymentType === 'principal_prepayment' && (
            <PrepaymentConsequenceFields
              loan={loan}
              events={events}
              eventEffectiveDate={effectiveDate}
              prepaymentAmount={prepaymentAmount ?? 0}
              value={prepaymentConsequence}
              onChange={setPrepaymentConsequence}
            />
          )}
        </>
      )}

      {eventType === 'balance_correction' && (
        <>
          <div className="field-group">
            <label htmlFor="licConfirmedBalance">LIC-Confirmed Outstanding Balance</label>
            <CurrencyInput id="licConfirmedBalance" value={licConfirmedBalance} onChange={setLicConfirmedBalance} />
            <span className="field-hint">
              From this date forward, this figure becomes the authoritative basis for calculation. Everything before
              this date remains exactly as previously calculated.
            </span>
          </div>
          <RepaymentConsequenceFields
            loan={loan}
            events={events}
            eventEffectiveDate={effectiveDate}
            value={balanceCorrectionConsequence}
            onChange={setBalanceCorrectionConsequence}
          />
        </>
      )}

      {eventType === 'charge' && (
        <>
          <div className="field-group">
            <label htmlFor="chargeAmount">Amount</label>
            <CurrencyInput id="chargeAmount" value={chargeAmount} onChange={setChargeAmount} />
          </div>
          <div className="field-group">
            <label htmlFor="category">Category</label>
            <select id="category" value={category} onChange={(e) => setCategory(e.target.value as ChargeEvent['category'])}>
              <option value="insurance">Insurance</option>
              <option value="processing_fee">Processing / Admin Fee</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="field-group">
            <label>
              <input
                type="checkbox"
                checked={financedIntoPrincipal}
                onChange={(e) => setFinancedIntoPrincipal(e.target.checked)}
                style={{ width: 'auto', marginRight: 8 }}
              />
              Financed into loan principal
            </label>
            <span className="field-hint">
              Only check this if your loan documentation confirms this charge was added to the principal. Otherwise
              it's tracked separately from principal/interest.
            </span>
          </div>
        </>
      )}

      {eventType === 'emi_date_change' && (
        <div className="field-group">
          <label htmlFor="newEmiDayOfMonth">New EMI Day of Month</label>
          <input
            id="newEmiDayOfMonth"
            type="number"
            min={1}
            max={28}
            value={newEmiDayOfMonth}
            onChange={(e) => setNewEmiDayOfMonth(e.target.value)}
          />
        </div>
      )}

      <div className="field-group">
        <label htmlFor="note">Note (optional)</label>
        <textarea id="note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>

      <div className="row">
        <button type="button" className="btn" onClick={onCancel} disabled={isSaving}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={isSaving}>
          {isSaving ? 'Saving…' : isEditing ? 'Save Changes' : 'Add Event'}
        </button>
      </div>
    </form>
  );
}
